import type { IGenericUserSuggestedUpdateClass } from '@bb/users';
import { UserSuggestedUpdateStatus, UserSuggestedUpdateType } from '@bb/users';
import { startCase } from 'lodash';

import type { UserState } from '../../UserViewPage/types';

type SuggestedLogItem = {
  key: string;

  date?: Date;
  from: string;
  status: UserSuggestedUpdateStatus;
  to: string;
  type: UserSuggestedUpdateType;
  user: string;
};

export const buildSuggestedLogItems = (
  suggestions: IGenericUserSuggestedUpdateClass[],
  user?: UserState,
) =>
  suggestions.reduce<SuggestedLogItem[]>((acc, suggestion) => {
    const { type } = suggestion;

    // initial automated suggestion
    const suggestedItem: SuggestedLogItem = {
      key: `${suggestion.createdAt}-suggestion-${suggestion.type}-${suggestion.newValue}`,
      status: UserSuggestedUpdateStatus.pending,
      type,
      from: '',
      to: '',
      date: suggestion.createdAt,
      user: 'BrokerBot',
    };

    switch (type) {
      case UserSuggestedUpdateType.names:
        suggestedItem.from = (suggestion.prevValue as string[])
          .join(' ')
          .trim();
        suggestedItem.to = (suggestion.newValue as string[]).join(' ').trim();
        break;
      default:
        suggestedItem.from = suggestion.prevValue as string;
        suggestedItem.to = suggestion.newValue as string;
    }

    acc.push(suggestedItem);

    if (
      // Accepted updates will be represented by the organic email diff item.
      // Besides accepted, the only other status with a log entry is dismissed.
      suggestion.status === UserSuggestedUpdateStatus.dismissed
    ) {
      const resolvedItem = {
        ...suggestedItem,
        key: `${suggestion.updatedAt}-suggestion-${suggestion.type}-${suggestion.newValue}`,
        date: suggestion.updatedAt,
        status: UserSuggestedUpdateStatus.dismissed,
        // to for dismissed suggestions will always be blank
        to: '',
        // We assume it's the user themselves that have taken an action
        user: `${user?.firstName} ${user?.lastName}`.trim(),
      };
      acc.push(resolvedItem);
    }

    return acc;
  }, []);

export const buildSuggestedUpdateLabel = (
  status: UserSuggestedUpdateStatus,
  type: UserSuggestedUpdateType,
) => {
  const label = startCase(type).replace(/s$/, '');
  if (status === UserSuggestedUpdateStatus.pending)
    return `Suggested New ${label}`;
  if (status === UserSuggestedUpdateStatus.dismissed)
    return `Kept Previous ${label}`;

  // should not happen;
  return '';
};
